import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PAYMENT_METHODS,
  paiseToRupees,
  parseRupees,
  type Payment,
  type PaymentMethod,
} from '@tailoring-crm/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Textarea,
  Label,
  useToast,
} from '@tailoring-crm/ui';
import { useRecordPayment } from '../../lib/payments/use-payments.js';
import { formatCurrency } from '../../lib/format.js';

/**
 * Taking money, at a counter, with a customer standing there.
 *
 * Everything here is arranged around that being a ten-second interaction. It opens on the FULL
 * OUTSTANDING amount and on the method this shop uses most, because the overwhelmingly common
 * case is "the balance, in the usual way" and that case should need one tap. The amount is
 * still a plain editable field, because the second most common case is a part payment and a
 * dialog that made that awkward would be worked around with a calculator.
 *
 * Taking MORE than the bill is possible and is never silent: the extra is named in rupees and
 * has to be ticked. That tick is what the API's `allowOverpayment` means — a shop deciding to
 * hold a credit, rather than a zero typed twice.
 */

export interface RecordPaymentDialogProps {
  /** The ledger it goes on: `orderPaymentsPath(…)` or `alterationPaymentsPath(…)`. */
  paymentsPath: string;
  /** The order's or the alteration's number, for the title. */
  number: string;
  kind: 'payment' | 'refund';
  totalPaise: number;
  paidPaise: number;
  outstandingPaise: number;
  suggestedMethod: PaymentMethod;
  onClose: () => void;
  /** Handed the entry that was written, so the caller can offer a receipt. */
  onRecorded?: (payment: Payment) => void;
}

/** `paiseToRupees` for a text input: no symbol, no grouping, no trailing `.00`. */
function rupeeInputValue(paise: number): string {
  if (paise <= 0) return '';
  const rupees = paiseToRupees(paise);
  return Number.isInteger(rupees) ? String(rupees) : rupees.toFixed(2);
}

/** Today, as `<input type="date">` wants it, in the shop's own timezone. */
function todayInputValue(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const get = (type: string): string => parts.find((part) => part.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

export function RecordPaymentDialog({
  paymentsPath,
  number,
  kind,
  totalPaise,
  paidPaise,
  outstandingPaise,
  suggestedMethod,
  onClose,
  onRecorded,
}: RecordPaymentDialogProps): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const record = useRecordPayment(paymentsPath);

  const today = todayInputValue();
  const [amount, setAmount] = useState(() =>
    kind === 'payment' ? rupeeInputValue(outstandingPaise) : '',
  );
  const [method, setMethod] = useState<PaymentMethod>(suggestedMethod);
  const [receivedOn, setReceivedOn] = useState(today);
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [allowOverpayment, setAllowOverpayment] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Rupees as typed, in paise. Unparsable counts as nothing rather than throwing mid-keystroke. */
  const amountPaise = ((): number => {
    if (amount.trim() === '') return 0;
    try {
      return parseRupees(amount);
    } catch {
      return 0;
    }
  })();

  const excessPaise = kind === 'payment' ? Math.max(0, amountPaise - outstandingPaise) : 0;
  const refundingTooMuch = kind === 'refund' && amountPaise > paidPaise;
  const canSubmit =
    amountPaise > 0 &&
    Number.isInteger(amountPaise) &&
    !refundingTooMuch &&
    (excessPaise === 0 || allowOverpayment);

  const submit = async (): Promise<void> => {
    setError(null);
    try {
      const result = await record.mutateAsync({
        amountPaise,
        method,
        kind,
        allowOverpayment: excessPaise > 0,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        // Today is sent as "now" rather than as midnight, so the day book reads in the order
        // the money actually came in. A backdated entry sends the date the shop chose.
        ...(receivedOn === today ? {} : { receivedAt: receivedOn }),
        label: formatCurrency(amountPaise),
      });

      if (result.queued) {
        // Not lost, only late — and the server will not apply it twice when it arrives.
        toast({ title: t('payments.queued'), description: t('payments.queuedHint') });
        onClose();
        return;
      }

      toast({
        title:
          kind === 'refund'
            ? t('payments.refundRecorded', { amount: formatCurrency(amountPaise) })
            : t('payments.recorded', { amount: formatCurrency(amountPaise) }),
        variant: 'success',
      });
      onRecorded?.(result.data.recorded);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('error.unexpected'));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {kind === 'refund'
              ? t('payments.refundTitle', { number })
              : t('payments.recordTitle', { number })}
          </DialogTitle>
          <DialogDescription>
            {kind === 'refund'
              ? t('payments.refundHint', { paid: formatCurrency(paidPaise) })
              : t('payments.recordHint', {
                  outstanding: formatCurrency(outstandingPaise),
                  total: formatCurrency(totalPaise),
                })}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {error ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
            >
              {error}
            </p>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="payment-amount">{t('payments.amount')}</Label>
            <Input
              id="payment-amount"
              inputMode="decimal"
              autoFocus
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
            {kind === 'payment' && outstandingPaise > 0 ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="self-start"
                onClick={() => setAmount(rupeeInputValue(outstandingPaise))}
              >
                {t('payments.fullBalance', { amount: formatCurrency(outstandingPaise) })}
              </Button>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="payment-method">{t('payments.method')}</Label>
            <select
              id="payment-method"
              value={method}
              onChange={(event) => setMethod(event.target.value as PaymentMethod)}
              className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
            >
              {PAYMENT_METHODS.map((one) => (
                <option key={one} value={one}>
                  {t(`payments.methods.${one}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="payment-date">{t('payments.receivedOn')}</Label>
              <Input
                id="payment-date"
                type="date"
                value={receivedOn}
                max={today}
                onChange={(event) => setReceivedOn(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="payment-reference">{t('payments.reference')}</Label>
              <Input
                id="payment-reference"
                value={reference}
                placeholder={t('payments.referencePlaceholder')}
                onChange={(event) => setReference(event.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="payment-note">{t('payments.note')}</Label>
            <Textarea
              id="payment-note"
              rows={2}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>

          {excessPaise > 0 ? (
            <label className="flex items-start gap-2 rounded-md border border-border bg-muted/50 p-3 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 size-4"
                checked={allowOverpayment}
                onChange={(event) => setAllowOverpayment(event.target.checked)}
              />
              <span>
                {t('payments.overpaymentConfirm', { amount: formatCurrency(excessPaise) })}
              </span>
            </label>
          ) : null}

          {refundingTooMuch ? (
            <p role="alert" className="text-sm font-medium text-destructive">
              {t('payments.refundTooMuch', { paid: formatCurrency(paidPaise) })}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={!canSubmit || record.isPending} onClick={() => void submit()}>
            {kind === 'refund' ? t('payments.refundConfirm') : t('payments.recordConfirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RecordPaymentDialog;
